import {
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface OutboundDeliveryOrderAddFormData {
  outType: string; // 'transfer_order' | 'sales_order'
  transferOrderId?: string; // Selected TO ID when outType is 'sales_order'
  transferOrderCode?: string; // Selected TO code
  outLogicWarehouseCode: string;
  outLogicWarehouseName: string;
  receivingLogicWarehouseCode?: string;
  receivingLogicWarehouseName?: string;
  description?: string;
}

export interface TransferOrderOption {
  id: string;
  code: string;
  supportWarehouseName: string;
  receiveWarehouseName: string;
  status: string;
  creationDate: string;
}

// ============================================================================
// OUTBOUND TYPE OPTIONS
// ============================================================================

export const OUTBOUND_TYPE_OPTIONS = [
  {
    label: 'outbound_management.outbound_type.transfer_order',
    value: 'transfer-out-stock',
  },
  {
    label: 'outbound_management.outbound_type.sales_order',
    value: 'order-sales-out-stock',
  },
  {
    label: 'outbound_management.outbound_type.purchase_return_order',
    value: 'purchase-order-return-out-stock',
  }
];

export const OUTBOUND_STATUS_OPTIONS = [
  { label: 'outbound_management.status.pending', value: 'PENDING' },
  { label: 'outbound_management.status.approved', value: 'APPROVED' },
  { label: 'outbound_management.status.shipped', value: 'SHIPPED' },
  { label: 'outbound_management.status.cancelled', value: 'CANCELED' },
];

// ============================================================================
// FORM FIELDS CONFIGURATION
// ============================================================================

export const OUTBOUND_DELIVERY_ORDER_ADD_FORM_CONFIG: FormField[] = [
  {
    label: 'outbound_management.fields.outbound_type',
    controlName: 'outType',
    type: 'dropdown',
    placeholder: 'outbound_management.placeholders.select_outbound_type',
    section: 'outbound_management.sections.basic_information',
    required: true,
    options: OUTBOUND_TYPE_OPTIONS,
    defaultValue: 'transfer-out-stock',
  },
  {
    label: 'outbound_management.fields.outbound_status',
    controlName: 'status',
    type: 'dropdown',
    placeholder: 'outbound_management.placeholders.select_status',
    section: 'outbound_management.sections.basic_information',
    required: false,
    disabled: true,
    options: OUTBOUND_STATUS_OPTIONS,
    defaultValue: 'PENDING',
  },
  {
    label: 'outbound_management.fields.reference_document',
    controlName: 'frontRecordCode',
    type: 'searchableDropdown',
    placeholder: 'outbound_management.placeholders.select_reference_document',
    section: 'outbound_management.sections.basic_information',
    required: true,
    options: [], // Will be populated dynamically with Transfer Orders
    showClear: true,
    hidden: true, // Shown when transfer_order or sales_order is selected
  },
  {
    label: 'outbound_management.fields.internal_order_number',
    controlName: 'orderCode',
    type: 'text',
    placeholder: 'outbound_management.placeholders.enter_internal_order_number',
    section: 'outbound_management.sections.basic_information',
    required: false,
  },
  {
    label: 'outbound_management.fields.scheduled_delivery_time',
    controlName: 'scheduledDeliveryTime',
    type: 'date',
    placeholder: 'outbound_management.placeholders.select_scheduled_delivery_time',
    section: 'outbound_management.sections.basic_information',
    required: false,
  },
  {
    label: 'outbound_management.fields.outbound_warehouse',
    controlName: 'outLogicWarehouseCode',
    type: 'warehouseAutocomplete',
    placeholder: 'outbound_management.placeholders.auto_filled_when_select_reference_document',
    section: 'outbound_management.sections.warehouse_information',
    required: false,
    disabled: true, // Disabled - auto-filled when reference document is selected
    readonly: true,
  },
  {
    label: 'outbound_management.fields.receiving_warehouse',
    controlName: 'receivingLogicWarehouseCode',
    type: 'warehouseAutocomplete',
    placeholder: 'outbound_management.placeholders.auto_filled_when_select_reference_document',
    section: 'outbound_management.sections.warehouse_information',
    required: false,
    disabled: true, // Disabled - auto-filled when reference document is selected
    readonly: true,
  },
  {
    label: 'outbound_management.fields.logistics_company',
    controlName: 'logisticCompany',
    type: 'text',
    placeholder: 'outbound_management.placeholders.enter_logistics_company',
    section: 'outbound_management.sections.logistics_information',
    required: false,
  },
  {
    label: 'outbound_management.fields.logistics_business',
    controlName: 'logisticBusiness',
    type: 'text',
    placeholder: 'outbound_management.placeholders.enter_logistics_business',
    section: 'outbound_management.sections.logistics_information',
    required: false,
  },
];

// ============================================================================
// TABLE SECTIONS CONFIGURATION (Shared between add and view)
// ============================================================================

export const OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'outbound_management.table.columns.spu_code',
    prop: 'spuCode',
    width: 150,
    type: 'text',
  },
  {
    label: 'outbound_management.table.columns.spu_name',
    prop: 'spuName',
    width: 200,
    type: 'text',
  },
  {
    label: 'outbound_management.table.columns.sku_code',
    prop: 'skuCode',
    width: 150,
    type: 'text',
  },
  {
    label: 'outbound_management.table.columns.sku_name',
    prop: 'skuName',
    width: 200,
    type: 'text',
  },
  {
    label: 'outbound_management.table.columns.quantity',
    prop: 'quantity',
    width: 120,
    type: 'text',
    align: 'right',
  },
  {
    label: 'outbound_management.table.columns.shipped_quantity',
    prop: 'alreadyOutQty',
    width: 120,
    type: 'text',
    align: 'right',
  },
];

export const OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_SECTION: TableSection = {
  title: 'outbound_management.sections.item_details',
  show: true,
  columns: OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS,
  data: [],
  hasCheckbox: false,
  actions: [],
};

// ============================================================================
// HEADER ACTIONS
// ============================================================================

export const OUTBOUND_DELIVERY_ORDER_ADD_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.save',
    action: 'save',
    severity: 'info',
  },
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    severity: 'secondary',
    outlined: true,
  },
];

// ============================================================================
// TRANSFER ORDER SELECTION DIALOG CONFIGURATION
// ============================================================================

export const TRANSFER_ORDER_SELECTION_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'outbound_management.table.columns.no',
    prop: 'serialNumber',
    width: 60,
    type: 'text',
    align: 'center',
  },
  {
    label: 'logic_transfer.table.columns.transfer_order_code',
    prop: 'code',
    width: 150,
    type: 'text',
  },
  {
    label: 'logic_transfer.table.columns.support_warehouse',
    prop: 'supportWarehouseName',
    width: 180,
    type: 'text',
  },
  {
    label: 'logic_transfer.table.columns.receive_warehouse',
    prop: 'receiveWarehouseName',
    width: 180,
    type: 'text',
  },
  {
    label: 'logic_transfer.table.columns.status',
    prop: 'status',
    width: 120,
    type: 'text',
  },
  {
    label: 'logic_transfer.table.columns.creation_date',
    prop: 'creationDate',
    width: 150,
    type: 'date',
  },
];

